import React, { useEffect, useRef, useState } from "react";

// ─── PROJECT DATA ────────────────────────────────────────────────────────────

const PROJECTS = [
  {
    id: 1,
    slug: "ai-sales-intelligence",
    category: "Artificial Intelligence · SaaS · Voice Analytics",
    categoryShort: "AI",
    index: "01",
    title: "Transforming Sales Calls into Actionable AI Insights",
    challenge:
      "Businesses lose revenue because valuable customer conversations go unanalyzed. Every call contains buying signals, objections, and coaching opportunities that disappear the moment the line goes dead.",
    solution:
      "An enterprise-grade AI platform that automatically records, transcribes, scores, and analyzes every sales interaction. Managers gain real-time visibility into agent performance, customer sentiment, objection handling, and conversion patterns — all from a single dashboard.",
    outcome: [
      { value: "34%", label: "Increase in close rate" },
      { value: "12×", label: "More calls reviewed" },
      { value: "60%", label: "Faster onboarding" },
    ],
    skills: ["AI", "Speech-to-Text", "Twilio", "Django", "React", "AWS", "OpenAI", "Deepgram", "WebSockets"],
    accent: "#1DA1F2",
    accentAlt: "#49D6FF",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v4M12 18v4M4 12H2M22 12h-2M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
        <circle cx="12" cy="12" r="5" />
      </svg>
    ),
    visual: "waveform",
  },
  {
    id: 2,
    slug: "enterprise-erp-transformation",
    category: "ERP · Operations · Business Automation",
    categoryShort: "ERP",
    index: "02",
    title: "A Single Source of Truth for Every Business Operation",
    challenge:
      "Disconnected spreadsheets, siloed departments, and manual data entry were costing a growing manufacturer hours every day — and hundreds of thousands in invisible inefficiencies.",
    solution:
      "A fully customized ERP platform unifying inventory, HR, accounting, procurement, CRM, and manufacturing under one automated backbone. Real-time operational insights replaced end-of-month guesswork.",
    outcome: [
      { value: "70%", label: "Reduction in manual entry" },
      { value: "3 days", label: "Saved per month in reporting" },
      { value: "1 system", label: "Replacing 7 tools" },
    ],
    skills: ["Odoo", "Python", "PostgreSQL", "Inventory", "Accounting", "Manufacturing", "CRM", "Automation"],
    accent: "#7C3AED",
    accentAlt: "#A78BFA",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
    visual: "grid",
  },
  {
    id: 3,
    slug: "smart-healthcare-management",
    category: "Healthcare · SaaS · Compliance",
    categoryShort: "Health",
    index: "03",
    title: "Clinical Software That Works the Way Doctors Do",
    challenge:
      "A busy multi-site clinic was drowning in paperwork — scheduling conflicts, duplicated patient records, and prescription errors were undermining both care quality and staff morale.",
    solution:
      "A secure, HIPAA-aligned healthcare platform for patient management, appointment scheduling, prescriptions, and medical records. Paperwork dropped, patient wait times fell, and staff shifted their attention back to patients.",
    outcome: [
      { value: "45%", label: "Fewer scheduling conflicts" },
      { value: "0", label: "Prescription duplication errors" },
      { value: "2 min", label: "Average record retrieval" },
    ],
    skills: ["Django", "DRF", "React", "AWS", "Authentication", "Security", "Scheduling"],
    accent: "#059669",
    accentAlt: "#34D399",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
    visual: "pulse",
  },
  {
    id: 4,
    slug: "ai-travel-assistant",
    category: "Artificial Intelligence · Travel · Personalization",
    categoryShort: "AI",
    index: "04",
    title: "Planning Smarter Journeys with Artificial Intelligence",
    challenge:
      "A travel platform was losing users to generic search engines — generic results with no memory of preferences, no context for decisions, and no sense of the traveler behind the query.",
    solution:
      "An AI-powered travel assistant that learns traveler preferences, recommends destinations and itineraries in natural language, and integrates real-time flight and hotel data — making complex trip planning feel effortless.",
    outcome: [
      { value: "3×", label: "Longer session time" },
      { value: "41%", label: "Higher booking conversion" },
      { value: "89%", label: "User satisfaction score" },
    ],
    skills: ["Machine Learning", "React", "Django", "Maps API", "Recommendation Engine", "OpenAI"],
    accent: "#D97706",
    accentAlt: "#FCD34D",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    visual: "globe",
  },
  {
    id: 5,
    slug: "ar-product-experience",
    category: "Augmented Reality · 3D · eCommerce",
    categoryShort: "AR / 3D",
    index: "05",
    title: "Let Customers See the Product Before They Buy",
    challenge:
      "A furniture retailer had a 28% return rate — customers couldn't visualize how pieces would look in their own space, so they ordered on hope and returned on reality.",
    solution:
      "Immersive WebAR experiences that place true-to-scale 3D product models in the customer's environment via their phone camera. No app download required. Confidence before checkout, delivered in the browser.",
    outcome: [
      { value: "28% → 9%", label: "Return rate" },
      { value: "2.2×", label: "Time spent per product" },
      { value: "18%", label: "Lift in average order value" },
    ],
    skills: ["Three.js", "React", "AR.js", "WebGL", "Zappar", "3D Modeling"],
    accent: "#DB2777",
    accentAlt: "#F472B6",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    visual: "cube",
  },
  {
    id: 6,
    slug: "cloud-migration-devops",
    category: "Cloud Infrastructure · DevOps · AWS",
    categoryShort: "Cloud",
    index: "06",
    title: "From Legacy Servers to Cloud Infrastructure That Never Sleeps",
    challenge:
      "A SaaS company was running on aging on-premise servers with no deployment automation, 6-hour maintenance windows, and no disaster recovery plan. Growth was being capped by infrastructure they'd outgrown.",
    solution:
      "A full cloud migration to AWS with containerized workloads, automated CI/CD pipelines, infrastructure-as-code, and 24/7 observability. Deploy in minutes, recover in seconds, scale without a call to engineering.",
    outcome: [
      { value: "99.97%", label: "Uptime post-migration" },
      { value: "6 hrs → 4 min", label: "Deployment time" },
      { value: "44%", label: "Reduction in infra costs" },
    ],
    skills: ["AWS", "Docker", "Terraform", "CI/CD", "EC2", "S3", "CloudWatch", "GitHub Actions"],
    accent: "#0891B2",
    accentAlt: "#22D3EE",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
      </svg>
    ),
    visual: "network",
  },
  {
    id: 7,
    slug: "ecommerce-automation",
    category: "eCommerce · Automation · ERP Integration",
    categoryShort: "Commerce",
    index: "07",
    title: "An Order Placed Anywhere, Fulfilled Everywhere — Automatically",
    challenge:
      "A multi-channel retailer was manually reconciling orders from Shopify, Amazon, and their own store into their ERP every night. Overselling, fulfillment delays, and sync errors were damaging customer trust.",
    solution:
      "A unified commerce automation layer connecting all storefronts, inventory, CRM, and fulfillment into one synchronized system. Orders route, inventory updates, and customers are notified — without anyone touching it.",
    outcome: [
      { value: "100%", label: "Automated order routing" },
      { value: "0", label: "Oversell incidents in 6 months" },
      { value: "52%", label: "Faster fulfillment" },
    ],
    skills: ["Shopify", "Odoo", "API Integration", "React", "Python", "Webhooks", "Automation"],
    accent: "#16A34A",
    accentAlt: "#4ADE80",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
    ),
    visual: "flow",
  },
  {
    id: 8,
    slug: "enterprise-analytics",
    category: "Business Intelligence · Data · React",
    categoryShort: "BI",
    index: "08",
    title: "Business Intelligence That Answers Before You Ask",
    challenge:
      "A finance team was generating 40-page monthly reports that by the time they were distributed, were already a month out of date. Leadership was making decisions on stale data.",
    solution:
      "Interactive real-time dashboards delivering live insights across sales, finance, and operations. Drill from company summary to individual transaction in seconds. Reports that update themselves.",
    outcome: [
      { value: "Real-time", label: "Data latency (was monthly)" },
      { value: "80%", label: "Fewer manual reports" },
      { value: "3 hrs", label: "Saved per week per analyst" },
    ],
    skills: ["React", "Chart.js", "D3", "Django", "PostgreSQL", "Data Visualization", "WebSockets"],
    accent: "#7C3AED",
    accentAlt: "#C4B5FD",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
    visual: "bars",
  },
  {
    id: 9,
    slug: "smart-manufacturing",
    category: "Manufacturing · Industry 4.0 · IoT",
    categoryShort: "MFG",
    index: "09",
    title: "When the Factory Floor Reports to You, Not the Other Way Around",
    challenge:
      "A manufacturer was discovering quality defects at end-of-line — after hours of production had already compounded the problem. Waste, rework, and downtime were eating margin.",
    solution:
      "A production monitoring system connecting IoT sensors, MRP, and quality control into a single live view. Anomalies surface in seconds, not hours. Scheduling and inventory respond automatically.",
    outcome: [
      { value: "67%", label: "Reduction in defect rate" },
      { value: "23%", label: "Less unplanned downtime" },
      { value: "2×", label: "Throughput improvement" },
    ],
    skills: ["Odoo MRP", "Python", "IoT Integration", "Automation", "React", "PostgreSQL", "MQTT"],
    accent: "#B45309",
    accentAlt: "#F59E0B",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    ),
    visual: "layers",
  },
  {
    id: 10,
    slug: "readal-digital-transformation",
    category: "AI · ERP · Cloud · Web · Automation",
    categoryShort: "All",
    index: "10",
    title: "Empowering Businesses Through Intelligent Technology",
    challenge:
      "At Readal Systems, we see the same pattern across industries: businesses with real potential, held back by the systems they've outgrown. Most agencies hand you a product. We design an ecosystem.",
    solution:
      "From AI-powered automation and enterprise ERP to cloud infrastructure and immersive web experiences — every system we build is engineered to connect, scale, and compound. One team, the full stack, zero handoff friction.",
    outcome: [
      { value: "120+", label: "Systems shipped" },
      { value: "98%", label: "Uptime SLA" },
      { value: "40%", label: "Avg. ops time saved" },
    ],
    skills: ["Artificial Intelligence", "Odoo ERP", "React & Next.js", "Django & Python", "AWS Cloud", "Three.js", "DevOps", "UI/UX"],
    accent: "#1DA1F2",
    accentAlt: "#49D6FF",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
    visual: "orbit",
    isFeatured: true,
  },
];

// ─── ANIMATED VISUALS ─────────────────────────────────────────────────────────

function VisualWaveform({ accent, accentAlt }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame;
    let t = 0;
    const draw = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const bars = 48;
      const barW = w / bars;
      for (let i = 0; i < bars; i++) {
        const phase = (i / bars) * Math.PI * 4 + t;
        const amp = 0.3 + 0.7 * Math.abs(Math.sin(phase));
        const barH = h * 0.15 + h * 0.65 * amp;
        const x = i * barW + barW * 0.15;
        const grd = ctx.createLinearGradient(0, h / 2 - barH / 2, 0, h / 2 + barH / 2);
        grd.addColorStop(0, accentAlt + "cc");
        grd.addColorStop(1, accent + "44");
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.roundRect(x, h / 2 - barH / 2, barW * 0.7, barH, 3);
        ctx.fill();
      }
      t += 0.04;
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [accent, accentAlt]);
  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

function VisualPulse({ accent, accentAlt }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const draw = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      ctx.beginPath();
      ctx.strokeStyle = accentAlt + "99";
      ctx.lineWidth = 2;
      ctx.moveTo(0, h / 2);
      for (let x = 0; x < w; x++) {
        const prog = x / w;
        let y = h / 2;
        if (prog > 0.2 && prog < 0.8) {
          const local = (prog - 0.2) / 0.6;
          y = h / 2 - Math.sin(local * Math.PI * 6 + t) * (h * 0.3) * Math.sin(local * Math.PI);
        }
        ctx.lineTo(x, y);
      }
      ctx.stroke();
      // glow
      ctx.strokeStyle = accent + "44";
      ctx.lineWidth = 6;
      ctx.stroke();
      t += 0.06;
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [accent, accentAlt]);
  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

function VisualBars({ accent, accentAlt }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const data = [0.6, 0.85, 0.45, 0.95, 0.7, 0.55, 0.88];
    const draw = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const pad = 16, barW = (w - pad * 2) / data.length - 8;
      data.forEach((val, i) => {
        const animated = val * Math.min(1, (Math.sin(t * 0.5 + i * 0.4) * 0.05 + 0.95));
        const bh = (h - pad * 2) * animated;
        const x = pad + i * ((w - pad * 2) / data.length) + 4;
        const y = h - pad - bh;
        const grd = ctx.createLinearGradient(0, y, 0, h - pad);
        grd.addColorStop(0, accentAlt + "ee");
        grd.addColorStop(1, accent + "33");
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.roundRect(x, y, barW, bh, [4, 4, 0, 0]);
        ctx.fill();
        // label line
        ctx.strokeStyle = accent + "22";
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(pad, y);
        ctx.lineTo(w - pad, y);
        ctx.stroke();
        ctx.setLineDash([]);
      });
      t += 0.03;
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [accent, accentAlt]);
  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

function VisualOrbit({ accent, accentAlt }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const draw = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const w = canvas.width, h = canvas.height;
      const cx = w / 2, cy = h / 2;
      ctx.clearRect(0, 0, w, h);
      // core glow
      const coreGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, 40);
      coreGrd.addColorStop(0, accentAlt + "ff");
      coreGrd.addColorStop(0.4, accent + "88");
      coreGrd.addColorStop(1, "transparent");
      ctx.fillStyle = coreGrd;
      ctx.beginPath();
      ctx.arc(cx, cy, 40, 0, Math.PI * 2);
      ctx.fill();
      // orbit rings
      [70, 100, 130].forEach((r, ri) => {
        ctx.strokeStyle = accent + "33";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(cx, cy, r, r * 0.4, ri * 0.5, 0, Math.PI * 2);
        ctx.stroke();
        // satellite dot
        const angle = t * (1 + ri * 0.3) + ri * 2;
        const sx = cx + r * Math.cos(angle);
        const sy = cy + r * 0.4 * Math.sin(angle);
        ctx.fillStyle = accentAlt;
        ctx.beginPath();
        ctx.arc(sx, sy, 4, 0, Math.PI * 2);
        ctx.fill();
        // trail
        ctx.strokeStyle = accentAlt + "55";
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let ti = 0; ti < 20; ti++) {
          const ta = angle - ti * 0.05;
          const tx2 = cx + r * Math.cos(ta);
          const ty2 = cy + r * 0.4 * Math.sin(ta);
          if (ti === 0) ctx.moveTo(tx2, ty2);
          else ctx.lineTo(tx2, ty2);
        }
        ctx.stroke();
      });
      t += 0.02;
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [accent, accentAlt]);
  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

function VisualGrid({ accent, accentAlt }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const draw = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const cols = 6, rows = 4;
      const cw = w / cols, ch = h / rows;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const phase = Math.sin(t + c * 0.8 + r * 1.2);
          const alpha = 0.08 + 0.18 * ((phase + 1) / 2);
          ctx.strokeStyle = `rgba(${parseInt(accentAlt.slice(1,3),16)},${parseInt(accentAlt.slice(3,5),16)},${parseInt(accentAlt.slice(5,7),16)},${alpha})`;
          ctx.lineWidth = 1;
          ctx.strokeRect(c * cw + 4, r * ch + 4, cw - 8, ch - 8);
          if (phase > 0.7) {
            const grd = ctx.createRadialGradient(c*cw+cw/2, r*ch+ch/2, 0, c*cw+cw/2, r*ch+ch/2, cw/2);
            grd.addColorStop(0, accent + "22");
            grd.addColorStop(1, "transparent");
            ctx.fillStyle = grd;
            ctx.fillRect(c * cw + 4, r * ch + 4, cw - 8, ch - 8);
          }
        }
      }
      t += 0.025;
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [accent, accentAlt]);
  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

function VisualNetwork({ accent, accentAlt }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const nodes = Array.from({ length: 10 }, (_, i) => ({
      x: 0.1 + Math.random() * 0.8,
      y: 0.1 + Math.random() * 0.8,
      vx: (Math.random() - 0.5) * 0.002,
      vy: (Math.random() - 0.5) * 0.002,
    }));
    const draw = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      nodes.forEach(n => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0.05 || n.x > 0.95) n.vx *= -1;
        if (n.y < 0.05 || n.y > 0.95) n.vy *= -1;
      });
      nodes.forEach((a, i) => {
        nodes.forEach((b, j) => {
          if (j <= i) return;
          const dx = (a.x - b.x) * w, dy = (a.y - b.y) * h;
          const dist = Math.sqrt(dx*dx + dy*dy);
          if (dist < 120) {
            const alpha = (1 - dist/120) * 0.5;
            ctx.strokeStyle = accent + Math.round(alpha*255).toString(16).padStart(2,"0");
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x*w, a.y*h);
            ctx.lineTo(b.x*w, b.y*h);
            ctx.stroke();
          }
        });
        ctx.fillStyle = accentAlt;
        ctx.beginPath();
        ctx.arc(a.x*w, a.y*h, 3, 0, Math.PI*2);
        ctx.fill();
      });
      t += 0.01;
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [accent, accentAlt]);
  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

function VisualFlow({ accent, accentAlt }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const draw = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const steps = 5;
      const stepW = w / (steps + 1);
      const nodes = Array.from({length: steps}, (_, i) => ({
        x: stepW * (i + 1), y: h / 2
      }));
      // connections
      nodes.forEach((node, i) => {
        if (i < nodes.length - 1) {
          const next = nodes[i+1];
          ctx.strokeStyle = accent + "44";
          ctx.lineWidth = 2;
          ctx.setLineDash([6, 4]);
          ctx.beginPath();
          ctx.moveTo(node.x + 16, node.y);
          ctx.lineTo(next.x - 16, next.y);
          ctx.stroke();
          ctx.setLineDash([]);
          // moving dot
          const prog = ((t * 0.6 + i * 0.2) % 1);
          const dx = next.x - 16 - (node.x + 16);
          const dotX = node.x + 16 + dx * prog;
          ctx.fillStyle = accentAlt;
          ctx.beginPath();
          ctx.arc(dotX, node.y, 4, 0, Math.PI * 2);
          ctx.fill();
        }
        // node circle
        const grd = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, 20);
        grd.addColorStop(0, accentAlt + "cc");
        grd.addColorStop(1, accent + "44");
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(node.x, node.y, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = accentAlt + "88";
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      t += 0.02;
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [accent, accentAlt]);
  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

function VisualGlobe({ accent, accentAlt }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const draw = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const w = canvas.width, h = canvas.height;
      const cx = w/2, cy = h/2, r = Math.min(w,h)*0.38;
      ctx.clearRect(0,0,w,h);
      // globe outline
      ctx.strokeStyle = accent + "55";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI*2);
      ctx.stroke();
      // lat lines
      for (let lat = -60; lat <= 60; lat += 30) {
        const ry = r * Math.sin(lat*Math.PI/180);
        const rx = r * Math.cos(lat*Math.PI/180);
        ctx.strokeStyle = accent + "22";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(cx, cy + ry*0, rx, Math.abs(ry)*0.4, 0, 0, Math.PI*2);
        ctx.stroke();
      }
      // lon lines animated
      for (let i = 0; i < 6; i++) {
        const angle = (i/6)*Math.PI + t*0.2;
        ctx.strokeStyle = accentAlt + "33";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(cx, cy, r * Math.abs(Math.cos(angle)), r, angle, 0, Math.PI*2);
        ctx.stroke();
      }
      // flight paths
      const paths = [
        {sx:0.2,sy:0.35,ex:0.75,ey:0.6},
        {sx:0.5,sy:0.25,ex:0.3,ey:0.65},
        {sx:0.65,sy:0.4,ex:0.15,ey:0.55},
      ];
      paths.forEach((p, pi) => {
        const prog = ((t*0.4 + pi*0.33) % 1);
        const sx = cx - r + p.sx * r*2;
        const sy = cy - r + p.sy * r*2;
        const ex = cx - r + p.ex * r*2;
        const ey = cy - r + p.ey * r*2;
        const mcx = (sx+ex)/2, mcy = Math.min(sy,ey) - r*0.3;
        // arc
        ctx.strokeStyle = accentAlt + "44";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.quadraticCurveTo(mcx, mcy, ex, ey);
        ctx.stroke();
        // dot
        const bx = (1-prog)*(1-prog)*sx + 2*(1-prog)*prog*mcx + prog*prog*ex;
        const by = (1-prog)*(1-prog)*sy + 2*(1-prog)*prog*mcy + prog*prog*ey;
        ctx.fillStyle = accentAlt;
        ctx.beginPath();
        ctx.arc(bx, by, 4, 0, Math.PI*2);
        ctx.fill();
      });
      t += 0.015;
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [accent, accentAlt]);
  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

function VisualCube({ accent, accentAlt }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const project = (x, y, z, w, h) => {
      const fov = 3;
      const pz = fov / (fov + z);
      return { x: w/2 + x*pz*80, y: h/2 + y*pz*80, s: pz };
    };
    const draw = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0,0,w,h);
      const s = Math.cos(t), c2 = Math.cos(t*0.7);
      const rotate = (x, y, z) => {
        // Y axis
        let nx = x*Math.cos(t) + z*Math.sin(t);
        let nz = -x*Math.sin(t) + z*Math.cos(t);
        // X axis
        let ny2 = y*Math.cos(t*0.6) - nz*Math.sin(t*0.6);
        let nz2 = y*Math.sin(t*0.6) + nz*Math.cos(t*0.6);
        return [nx, ny2, nz2];
      };
      const v = [-1,1].flatMap(x => [-1,1].flatMap(y => [-1,1].map(z => rotate(x,y,z))));
      const edges = [[0,1],[0,2],[0,4],[1,3],[1,5],[2,3],[2,6],[3,7],[4,5],[4,6],[5,7],[6,7]];
      edges.forEach(([a,b]) => {
        const pa = project(...v[a], w, h);
        const pb = project(...v[b], w, h);
        const alpha = Math.max(0.1, Math.min(0.6, (pa.s + pb.s)/2));
        ctx.strokeStyle = accentAlt + Math.round(alpha*255).toString(16).padStart(2,"0");
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      });
      t += 0.018;
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [accent, accentAlt]);
  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

function VisualLayers({ accent, accentAlt }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const draw = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0,0,w,h);
      const layers = 4;
      const lh = (h * 0.7) / layers;
      const baseY = h * 0.15;
      for (let i = layers-1; i >= 0; i--) {
        const y = baseY + i * lh + Math.sin(t + i*0.8)*4;
        const layerW = w * (0.5 + i * 0.1);
        const lx = (w - layerW)/2;
        const alpha = 0.3 + i*0.15;
        ctx.fillStyle = (i % 2 === 0 ? accent : accentAlt) + Math.round(alpha*255).toString(16).padStart(2,"0");
        ctx.beginPath();
        ctx.roundRect(lx, y, layerW, lh*0.65, 6);
        ctx.fill();
        ctx.strokeStyle = accentAlt + "55";
        ctx.lineWidth = 1;
        ctx.stroke();
        // data moving across layer
        const dotX = lx + ((t*60 + i*40) % layerW);
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(dotX, y + lh*0.32, 3, 0, Math.PI*2);
        ctx.fill();
      }
      t += 0.02;
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [accent, accentAlt]);
  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

const VISUALS = {
  waveform: VisualWaveform,
  pulse: VisualPulse,
  bars: VisualBars,
  orbit: VisualOrbit,
  grid: VisualGrid,
  network: VisualNetwork,
  flow: VisualFlow,
  globe: VisualGlobe,
  cube: VisualCube,
  layers: VisualLayers,
};

// ─── FILTER TABS ──────────────────────────────────────────────────────────────

const FILTERS = ["All", "AI", "ERP", "Cloud", "Commerce", "BI", "Health", "AR / 3D", "MFG"];

// ─── CARD ─────────────────────────────────────────────────────────────────────

function ProjectCard({ project, index }) {
  const Visual = VISUALS[project.visual] || VisualOrbit;
  const cardRef = useRef(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) el.classList.add("pf-in"); },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <article
      ref={cardRef}
      className="pf-card pf-reveal"
      style={{ "--delay": `${(index % 3) * 0.08}s`, "--accent": project.accent, "--accent-alt": project.accentAlt }}
    >
      {/* Visual */}
      <div className="pf-visual">
        <Visual accent={project.accent} accentAlt={project.accentAlt} />
        <div className="pf-index">{project.index}</div>
        <div className="pf-category-tag">{project.categoryShort}</div>
      </div>

      {/* Content */}
      <div className="pf-body">
        <div className="pf-eyebrow">{project.category}</div>
        <h3 className="pf-title">{project.title}</h3>

        <div className="pf-problem">
          <span className="pf-section-label">THE PROBLEM</span>
          <p>{project.challenge}</p>
        </div>

        <div className="pf-solution">
          <span className="pf-section-label">THE SOLUTION</span>
          <p>{project.solution}</p>
        </div>

        {/* Outcomes */}
        <div className="pf-outcomes">
          {project.outcome.map((o, i) => (
            <div key={i} className="pf-outcome">
              <div className="pf-outcome-val">{o.value}</div>
              <div className="pf-outcome-label">{o.label}</div>
            </div>
          ))}
        </div>

        {/* Skills */}
        <div className="pf-skills">
          {project.skills.map((s) => (
            <span key={s} className="pf-skill">{s}</span>
          ))}
        </div>
      </div>
    </article>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function PortfolioPage() {
  const [activeFilter, setActiveFilter] = useState("All");
  const heroRef = useRef(null);

  const filtered = PROJECTS.filter(p =>
    activeFilter === "All" || p.categoryShort === activeFilter
  );

  useEffect(() => {
    // Stagger reveal for filter change
  }, [activeFilter]);

  return (
    <div className="readal-root pf-root">
      <style>{CSS}</style>

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="pf-hero" ref={heroRef}>
        <div className="pf-hero-bg">
          {Array.from({ length: 30 }).map((_, i) => (
            <div key={i} className="pf-particle" style={{
              left: `${Math.random()*100}%`,
              top: `${Math.random()*100}%`,
              animationDelay: `${Math.random()*4}s`,
              animationDuration: `${3 + Math.random()*4}s`,
            }} />
          ))}
        </div>
        <div className="container pf-hero-inner">
          <div className="eyebrow">Our Work · Case Studies · Impact</div>
          <h1 className="pf-hero-h1">
            We don't build software.<br />
            <span>We solve problems.</span>
          </h1>
          <p className="pf-hero-sub">
            Ten case studies across AI, ERP, Cloud, and beyond — each one a business problem turned into a system that compounds.
          </p>
          <div className="pf-hero-stats">
            <div className="pf-hs">
              <span className="pf-hs-val">120+</span>
              <span className="pf-hs-label">Systems Shipped</span>
            </div>
            <div className="pf-hs-div" />
            <div className="pf-hs">
              <span className="pf-hs-val">10</span>
              <span className="pf-hs-label">Industries</span>
            </div>
            <div className="pf-hs-div" />
            <div className="pf-hs">
              <span className="pf-hs-val">40%</span>
              <span className="pf-hs-label">Avg. Ops Time Saved</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FILTERS ──────────────────────────────────────────────── */}
      <div className="pf-filters-wrap">
        <div className="container pf-filters">
          {FILTERS.map(f => (
            <button
              key={f}
              className={`pf-filter-btn${activeFilter === f ? " pf-filter-active" : ""}`}
              onClick={() => setActiveFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* ── GRID ─────────────────────────────────────────────────── */}
      <section className="pf-grid-section">
        <div className="container">
          {filtered.length === 0 ? (
            <div className="pf-empty">No projects match this filter.</div>
          ) : (
            <div className="pf-grid">
              {filtered.map((p, i) => (
                <ProjectCard key={p.id} project={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <section className="pf-cta-section">
        <div className="container">
          <div className="pf-cta-panel">
            <div className="pf-cta-glow" />
            <div className="eyebrow">Ready to be next?</div>
            <h2>Tell us your problem.<br />We'll build the system.</h2>
            <p>Every engagement starts with a conversation — not a proposal. Let's map what's holding your business back and what a solution actually looks like.</p>
            <div className="pf-cta-btns">
              <a href="#contact" className="btn btn-primary magnetic">Book a Discovery Call</a>
              <a href="#services" className="btn btn-ghost magnetic">Explore Services</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500&family=JetBrains+Mono:wght@400;500&display=swap');

.pf-root {
  position: relative;
  z-index: 0;
}

/* ── HERO ──────────────────────────────────────────────── */
.pf-hero {
  position: relative;
  padding: 160px 0 100px;
  text-align: center;
  overflow: hidden;
  border-bottom: 1px solid var(--border);
}

.pf-hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.pf-particle {
  position: absolute;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: var(--secondary);
  opacity: 0;
  animation: pf-float linear infinite;
}

@keyframes pf-float {
  0%   { opacity: 0; transform: translateY(0) scale(1); }
  20%  { opacity: 0.7; }
  80%  { opacity: 0.3; }
  100% { opacity: 0; transform: translateY(-80px) scale(0); }
}

.pf-hero-inner {
  position: relative;
  z-index: 2;
}

.pf-hero-h1 {
  font-size: clamp(36px, 6vw, 68px);
  line-height: 1.06;
  margin: 22px auto 20px;
  max-width: 780px;
}

.pf-hero-h1 span {
  background: var(--grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.pf-hero-sub {
  font-size: 18px;
  max-width: 520px;
  margin: 0 auto 48px;
  color: var(--gray);
}

.pf-hero-stats {
  display: inline-flex;
  align-items: center;
  gap: 0;
  background: rgba(255,255,255,.04);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 0 8px;
  backdrop-filter: blur(14px);
}

.pf-hs {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 28px;
}

.pf-hs-val {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 28px;
  color: var(--secondary);
}

.pf-hs-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--gray);
  letter-spacing: .06em;
  margin-top: 4px;
  text-transform: uppercase;
}

.pf-hs-div {
  width: 1px;
  height: 40px;
  background: var(--border);
}

/* ── FILTERS ────────────────────────────────────────────── */
.pf-filters-wrap {
  position: sticky;
  top: 61px;
  z-index: 90;
  background: rgba(5,7,10,.85);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--border);
  padding: 14px 0;
}

.pf-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pf-filter-btn {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--gray);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 7px 16px;
  cursor: pointer;
  transition: all .25s;
}

.pf-filter-btn:hover {
  color: var(--off-white);
  border-color: rgba(73,214,255,.4);
}

.pf-filter-active {
  color: var(--secondary) !important;
  border-color: var(--secondary) !important;
  background: rgba(73,214,255,.08) !important;
  box-shadow: 0 0 16px rgba(73,214,255,.15);
}

/* ── GRID ───────────────────────────────────────────────── */
.pf-grid-section {
  padding: 72px 0 80px;
}

.pf-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

/* ── CARD ───────────────────────────────────────────────── */
.pf-reveal {
  opacity: 0;
  transform: translateY(40px);
  filter: blur(5px);
  transition: opacity .85s cubic-bezier(.16,1,.3,1) var(--delay, 0s),
              transform .85s cubic-bezier(.16,1,.3,1) var(--delay, 0s),
              filter .85s cubic-bezier(.16,1,.3,1) var(--delay, 0s);
}

.pf-in {
  opacity: 1 !important;
  transform: translateY(0) !important;
  filter: blur(0) !important;
}

.pf-card {
  background: rgba(255,255,255,.03);
  border: 1px solid var(--border);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform .45s cubic-bezier(.16,1,.3,1),
              box-shadow .45s cubic-bezier(.16,1,.3,1),
              border-color .45s;
  position: relative;
}

.pf-card::before {
  content: '';
  position: absolute;
  top: -1px; left: -1px; right: -1px;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--accent-alt, #49D6FF) 50%, transparent);
  opacity: 0;
  transition: opacity .4s;
}

.pf-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 0 40px color-mix(in srgb, var(--accent, #1DA1F2) 30%, transparent);
  border-color: color-mix(in srgb, var(--accent-alt, #49D6FF) 50%, transparent);
}

.pf-card:hover::before {
  opacity: 1;
}

/* Visual area */
.pf-visual {
  position: relative;
  height: 180px;
  background: radial-gradient(ellipse at 50% 0%, color-mix(in srgb, var(--accent, #1DA1F2) 18%, transparent), transparent 70%),
              var(--card);
  border-bottom: 1px solid var(--border);
  overflow: hidden;
}

.pf-visual canvas {
  width: 100% !important;
  height: 100% !important;
}

.pf-index {
  position: absolute;
  top: 14px;
  left: 16px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--gray);
  letter-spacing: .1em;
}

.pf-category-tag {
  position: absolute;
  top: 12px;
  right: 14px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--accent, #1DA1F2);
  background: color-mix(in srgb, var(--accent, #1DA1F2) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent, #1DA1F2) 35%, transparent);
  border-radius: 6px;
  padding: 4px 10px;
}

/* Card body */
.pf-body {
  padding: 26px 28px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
}

.pf-eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: var(--accent, #1DA1F2);
}

.pf-title {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.25;
  color: var(--white);
  letter-spacing: -0.01em;
}

.pf-section-label {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--gray);
  margin-bottom: 6px;
}

.pf-problem p,
.pf-solution p {
  font-size: 14px;
  color: var(--gray);
  line-height: 1.6;
}

/* Outcomes */
.pf-outcomes {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 4px;
}

.pf-outcome {
  background: rgba(255,255,255,.04);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 10px 8px;
  text-align: center;
}

.pf-outcome-val {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: var(--accent, #1DA1F2);
  line-height: 1.1;
}

.pf-outcome-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: var(--gray);
  letter-spacing: .04em;
  margin-top: 4px;
  line-height: 1.3;
}

/* Skills */
.pf-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.pf-skill {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--light-gray);
  background: rgba(255,255,255,.05);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 3px 9px;
  letter-spacing: .03em;
}

/* ── CTA ────────────────────────────────────────────────── */
.pf-cta-section {
  padding: 0 0 120px;
}

.pf-cta-panel {
  position: relative;
  border-radius: 28px;
  padding: 80px 48px;
  text-align: center;
  background: var(--card);
  border: 1px solid var(--border);
  overflow: hidden;
}

.pf-cta-glow {
  position: absolute;
  width: 700px;
  height: 700px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(29,161,242,.2), transparent 65%);
  top: -380px;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
}

.pf-cta-panel h2 {
  font-size: clamp(28px, 4vw, 44px);
  line-height: 1.15;
  position: relative;
  z-index: 2;
  margin: 16px 0 16px;
}

.pf-cta-panel p {
  position: relative;
  z-index: 2;
  max-width: 480px;
  margin: 0 auto 36px;
  font-size: 16px;
}

.pf-cta-btns {
  display: flex;
  gap: 14px;
  justify-content: center;
  position: relative;
  z-index: 2;
}

.pf-empty {
  text-align: center;
  color: var(--gray);
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  padding: 80px 0;
}

/* ── RESPONSIVE ─────────────────────────────────────────── */
@media (max-width: 1100px) {
  .pf-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 720px) {
  .pf-grid {
    grid-template-columns: 1fr;
  }

  .pf-hero {
    padding: 120px 0 80px;
  }

  .pf-hero-stats {
    flex-direction: column;
    padding: 8px 24px;
  }

  .pf-hs-div {
    width: 80px;
    height: 1px;
  }

  .pf-cta-panel {
    padding: 48px 24px;
  }

  .pf-cta-btns {
    flex-direction: column;
    align-items: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pf-particle,
  .pf-reveal {
    animation: none !important;
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
    filter: none !important;
  }
}
`;